import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Zap, ThumbsUp, ThumbsDown, Share2, Bookmark, MessageSquare, Flag, ChevronDown, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import MPlayShell, { Avatar, fmtViews, timeAgo } from "./VidShared";

export default function ShortsPage() {
  const [shorts, setShorts] = useState(null);
  const [active, setActive] = useState(0);
  const [liked, setLiked] = useState({});
  const containerRef = useRef(null);

  useEffect(() => {
    api.get("/vid/home").then((r) => setShorts(r.data.shorts || [])).catch(() => setShorts([]));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const h = el.clientHeight;
      setActive(Math.round(el.scrollTop / h));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [shorts]);

  const like = async (v) => {
    const has = liked[v.id];
    setLiked({ ...liked, [v.id]: !has });
    try {
      const { data } = await api.post(`/vid/videos/${v.id}/like`, { unlike: has });
      setShorts((s) => s.map((x) => x.id === v.id ? { ...x, likes: data.likes } : x));
    } catch { /* like endpoints may require auth — keep optimistic */ }
  };

  const share = async (v) => {
    const url = `${window.location.origin}/v/${v.slug}`;
    try { await navigator.share({ title: v.title, url }); }
    catch { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };

  return (
    <MPlayShell noPad>
      <div ref={containerRef} className="mp-snap h-[calc(100dvh-60px)] lg:h-[calc(100vh-60px)] overflow-y-auto mp-scroll pb-16 lg:pb-0" data-testid="mplay-shorts-feed">
        {!shorts && <div className="h-full flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>}
        {shorts?.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-white/50">
            <Zap className="h-10 w-10" /><p className="font-bold">No shorts yet</p>
          </div>
        )}
        {shorts?.map((v, i) => (
          <div key={v.id} className="h-[calc(100dvh-60px)] lg:h-[calc(100vh-60px)] flex items-center justify-center py-2 lg:py-4" data-testid={`mp-short-${v.id}`}>
            <div className="mp-kw relative h-full max-h-[820px] aspect-[9/16] rounded-none sm:rounded-2xl overflow-hidden bg-black shadow-2xl">
              {i === active ? (
                <iframe src={`${v.embed_url}${v.embed_url.includes("?") ? "&" : "?"}autoplay=1&mute=1&playsinline=1`} title={v.title}
                  className="h-full w-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
              ) : (
                <>
                  {v.thumbnail_url
                    ? <img src={v.thumbnail_url} alt={v.title} className="h-full w-full object-cover" />
                    : <div className="h-full w-full bg-[#101a14]" />}
                  <button onClick={() => setActive(i)} className="absolute inset-0 flex items-center justify-center bg-black/30" data-testid={`mp-short-play-${v.id}`}>
                    <span className="h-16 w-16 rounded-full bg-black/60 backdrop-blur flex items-center justify-center"><Play className="h-7 w-7 text-white fill-white ml-1" /></span>
                  </button>
                </>
              )}
              {/* right action rail */}
              <div className="absolute right-2.5 bottom-24 flex flex-col items-center gap-5">
                <button onClick={() => like(v)} className="flex flex-col items-center gap-1" data-testid={`mp-short-like-${v.id}`}>
                  <span className={`h-11 w-11 rounded-full flex items-center justify-center ${liked[v.id] ? "mp-green" : "bg-black/50 backdrop-blur text-white"}`}>
                    <ThumbsUp className={`h-5 w-5 ${liked[v.id] ? "fill-current" : ""}`} /></span>
                  <span className="text-white text-[11px] font-bold">{fmtViews((v.likes || 0) + (liked[v.id] ? 1 : 0))}</span>
                </button>
                <Link to={`/v/${v.slug}`} className="flex flex-col items-center gap-1" data-testid={`mp-short-comments-${v.id}`}>
                  <span className="h-11 w-11 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center"><MessageSquare className="h-5 w-5" /></span>
                  <span className="text-white text-[11px] font-bold">{fmtViews(v.comment_count || 0)}</span>
                </Link>
                <button onClick={() => share(v)} className="flex flex-col items-center gap-1" data-testid={`mp-short-share-${v.id}`}>
                  <span className="h-11 w-11 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center"><Share2 className="h-5 w-5" /></span>
                  <span className="text-white text-[11px] font-bold">Share</span>
                </button>
              </div>
              {/* bottom info */}
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
                <Link to={`/v/${v.slug}`} className="flex items-center gap-2.5 mb-2" data-testid={`mp-short-creator-${v.id}`}>
                  <Avatar name={v.creator_name} src={v.creator_avatar} size={32} />
                  <span className="text-white text-[13px] font-bold truncate">{v.creator_name}</span>
                </Link>
                <p className="text-white text-[13px] font-semibold leading-snug line-clamp-2 pr-16">{v.title}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </MPlayShell>
  );
}
